import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Users, GitBranch, ChevronDown, ChevronRight, UserCheck, UserX, Target, TrendingUp, ArrowUp } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MatrixTree = () => {
  const { user } = useAuth();
  const [treeData, setTreeData] = useState(null);
  const [downlineStats, setDownlineStats] = useState(null);
  const [position, setPosition] = useState(null);
  const [upline, setUpline] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [treeDepth, setTreeDepth] = useState(3);

  useEffect(() => {
    fetchMatrixData();
  }, [treeDepth]);

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const [treeRes, statsRes, posRes, uplineRes, configRes] = await Promise.all([
        api.get(`/matrix/my-tree?depth=${treeDepth}`),
        api.get('/matrix/my-downline'),
        api.get('/matrix/my-position'),
        api.get('/matrix/my-upline'),
        api.get('/matrix/config')
      ]);
      setTreeData(treeRes.data);
      setDownlineStats(statsRes.data);
      setPosition(posRes.data);
      setUpline(uplineRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Failed to fetch matrix data:', error);
      toast.error('Failed to load matrix data');
    } finally {
      setLoading(false);
    }
  };

  // Tree Node Component
  const TreeNode = ({ node, level = 0 }) => {
    const [expanded, setExpanded] = useState(level < 2);
    
    if (!node) return null;
    
    const hasChildren = node.children && node.children.length > 0;
    const isActivated = node.user?.is_activated;
    
    return (
      <div className="ml-4" data-testid={`tree-node-${node.user?.username}`}>
        <div 
          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-white/10 transition-all ${
            isActivated ? 'bg-green-500/10' : 'bg-white/5'
          }`}
          onClick={() => hasChildren && setExpanded(!expanded)}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )
          ) : (
            <div className="w-4" />
          )}
          
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isActivated ? 'bg-green-500/20' : 'bg-muted/20'
          }`}>
            {isActivated ? (
              <UserCheck className="w-4 h-4 text-green-500" />
            ) : (
              <UserX className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          
          <div className="flex-1">
            <p className="font-semibold text-sm">{node.user?.name || 'Unknown'}</p>
            <p className="text-xs text-muted-foreground">@{node.user?.username}</p>
          </div>
          
          {node.child_count > 0 && (
            <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
              {node.child_count}/{config?.width || 10}
            </span>
          )}
        </div>
        
        {expanded && hasChildren && (
          <div className="border-l border-white/10 ml-4">
            {node.children.map((child, idx) => (
              <TreeNode key={child.user?.id || idx} node={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-xl">Loading matrix data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="matrix-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">My Matrix</h1>
        <p className="text-muted-foreground mb-8">
          {config?.description || '10x10 Matrix with BFS Spillover'}
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* My Position */}
          <div className="glass-card p-6 border-2 border-primary/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">My Position</h3>
                <p className="text-xs text-muted-foreground">In the matrix</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-primary" data-testid="matrix-level">
              Level {position?.level || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Position {position?.position || 0}
            </p>
          </div>

          {/* Direct Members */}
          <div className="glass-card p-6 border-2 border-blue-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Direct Members</h3>
                <p className="text-xs text-muted-foreground">First level</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-blue-500" data-testid="direct-count">
              {downlineStats?.direct_count || 0}/{config?.width || 10}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {downlineStats?.direct_slots_available || 10} slots available
            </p>
          </div>

          {/* Total Downline */}
          <div className="glass-card p-6 border-2 border-green-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Total Team</h3>
                <p className="text-xs text-muted-foreground">All levels</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-green-500" data-testid="total-downline">
              {treeData?.stats?.total_downline || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {treeData?.stats?.active_downline || 0} activated
            </p>
          </div>

          {/* Upline Depth */}
          <div className="glass-card p-6 border-2 border-purple-500/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <ArrowUp className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Upline Depth</h3>
                <p className="text-xs text-muted-foreground">To top</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-purple-500" data-testid="upline-depth">
              {upline?.depth || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Levels above you
            </p>
          </div>
        </div>

        {/* My Sponsor & Parent Info */}
        {(position?.sponsor || position?.parent) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {position?.sponsor && (
              <div className="glass-card p-6" data-testid="sponsor-info">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  My Sponsor (Who Referred Me)
                </h3>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="font-bold text-lg">{position.sponsor.name}</p>
                  <p className="text-muted-foreground">@{position.sponsor.username}</p>
                </div>
              </div>
            )}
            
            {position?.parent && position?.is_spillover && (
              <div className="glass-card p-6" data-testid="parent-info">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-accent" />
                  My Matrix Parent (Spillover)
                </h3>
                <div className="bg-accent/10 border border-accent/30 rounded-xl p-4">
                  <p className="font-bold text-lg">{position.parent.name}</p>
                  <p className="text-muted-foreground">@{position.parent.username}</p>
                  <p className="text-xs text-accent mt-2">You were placed here via BFS spillover</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Downline by Level */}
        {downlineStats?.by_level && Object.keys(downlineStats.by_level).length > 0 && (
          <div className="glass-card p-6 mb-8" data-testid="level-breakdown">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-green-500" />
              Team by Level
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
              {Array.from({ length: config?.depth || 10 }, (_, i) => i + 1).map(level => {
                const levelData = downlineStats.by_level[level] || { total: 0, active: 0 };
                return (
                  <div key={level} className="bg-white/5 rounded-xl p-3 text-center">
                    <p className="text-xs text-muted-foreground">L{level}</p>
                    <p className="text-xl font-bold text-primary">{levelData.total}</p>
                    <p className="text-xs text-green-500">{levelData.active} active</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upline Path */}
        {upline?.upline_path && upline.upline_path.length > 0 && (
          <div className="glass-card p-6 mb-8" data-testid="upline-path">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <ArrowUp className="w-5 h-5 text-purple-500" />
              My Upline Path
            </h2>
            <div className="flex flex-wrap gap-2">
              {upline.upline_path.map((member, idx) => (
                <div 
                  key={idx}
                  className={`px-4 py-2 rounded-full text-sm ${
                    member.is_activated 
                      ? 'bg-green-500/20 text-green-500 border border-green-500/30' 
                      : 'bg-white/5 text-muted-foreground border border-white/10'
                  }`}
                >
                  <span className="font-semibold">{member.name}</span>
                  <span className="text-xs ml-2">L{idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matrix Tree Visualization */}
        <div className="glass-card p-6" data-testid="matrix-tree">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-primary" />
              My Matrix Tree
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Depth:</span>
              <select
                value={treeDepth}
                onChange={(e) => setTreeDepth(Number(e.target.value))}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-sm"
                data-testid="tree-depth-select"
              >
                <option value={2}>2 Levels</option>
                <option value={3}>3 Levels</option>
                <option value={4}>4 Levels</option>
                <option value={5}>5 Levels</option>
              </select>
            </div>
          </div>
          
          {treeData?.tree ? (
            <div className="overflow-x-auto">
              <TreeNode node={treeData.tree} />
            </div>
          ) : (
            <div className="text-center py-12">
              <GitBranch className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-xl font-semibold mb-2">No Team Members Yet</p>
              <p className="text-muted-foreground">
                Share your referral link to start building your matrix team!
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <UserCheck className="w-2.5 h-2.5 text-green-500" />
            </div>
            <span className="text-muted-foreground">Activated Member</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-muted/20 flex items-center justify-center">
              <UserX className="w-2.5 h-2.5 text-muted-foreground" />
            </div>
            <span className="text-muted-foreground">Free Member</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatrixTree;
